<template>
  <div class="play-launch-view" v-if="canView('playbooks')">
    <!-- Show Deployment Checklist after launch -->
    <deployment-checklist 
      v-if="if_deployed"
      :deployment-data="deploymentDataForChecklist"
      :playbook="playbook"
      :client="client">
    </deployment-checklist>
    
    <!-- Show Launch View by default -->
    <div class="row" v-else>
      <!-- Left Column -->
      <div class="col-md-5 col-12">
        <!-- Launch Summary Card -->
        <div class="card mb-3 launch-summary-card">
            <div class="card-header d-flex justify-content-between align-items-center">
                <h4 class="mb-0">{{ playbook.name }}</h4>
                <button class="btn btn-secondary" @click="goBackToBuilder">
                    <img src="/img/icons/avid-icons/chevron-left.svg" class="icon" alt="">
                    Back To Play Builder                    
                </button>
            </div>
            <div class="card-body">
                <h4 class="mb-0">You're about to launch</h4>
                  <div class="d-flex align-items-center mb-3">
                      <span class="h3 text-color-blue me-2">{{ selectedTouchpointCount }}</span>
                      <span class="h3">touchpoint{{ selectedTouchpointCount === 1 ? '' : 's' }}</span>
                  </div>
                <p>We'll place them in your connected system, ready for you to launch. If a system isn't connected, we'll generate a Google Doc for quick upload.</p>                
            </div>
        </div>

        <!-- Touchpoints Section -->
        <div class="card mb-3 touchpoints-card">
            <div class="card-header">
                <h4>Touchpoints</h4>
            </div>
            <div class="card-body">
                <!-- Dynamic Touchpoints grouped by asset_type_id -->
                <div v-for="(group, assetTypeId) in grouped_assets" :key="assetTypeId" class="touchpoint-item d-flex flex-wrap align-items-center justify-content-between mb-3"
                 @click="selectGroup(assetTypeId)" :class="{ 'selected': selected_group_id === assetTypeId, 'disabled': hasLoadingConnectionForGroup(assetTypeId) }">
                    <div class="touchpoint-info d-flex align-items-center">
                        <div class="touchpoint-icon me-3">
                            <img :src="'/img/icons/avid-icons/' + group[0]?.asset_type.icon_class + '.svg'" class="asset-type-icon icon" alt=""></img>
                        </div>
                        <span class="touchpoint-name">{{ group[0]?.asset_type?.name }}</span>
                        <div v-if="group.length > 1" class="badge badge-blue ms-2">{{ group.length }}</div>
                    </div>
                    <div class="touchpoint-actions d-flex align-items-center justify-content-end w-100 w-sm-auto mt-2 mt-sm-0">                        
                        <!-- Status badges -->
                        <div class="status-badges d-flex flex-wrap gap-2">
                            <span class="badge" :class="getConnectionStatusClass(assetTypeId)">
                                <i v-if="hasLoadingConnectionForGroup(assetTypeId)" class="fas fa-spinner fa-spin me-1"></i>
                                <i v-else :class="getConnectionStatusIcon(assetTypeId)" class="me-1"></i>
                                {{ getConnectionStatus(assetTypeId) }}
                            </span>
                            <span class="badge" :class="getStatusClass(assetTypeId)">
                                <i :class="getStatusIcon(assetTypeId)" class="me-1"></i>
                                {{ getStatus(assetTypeId) }}
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Launch Button -->
            <div class="card-footer d-flex justify-content-end">
                <button class="btn btn-primary btn-primary-playbook" 
                    @click="launchPlay" v-if="canExport('playbooks')"
                    :disabled="!allGroupsReady || launching">
                    <img v-if="!launching" src="/img/icons/avid-icons/rocket-02.svg" class="icon me-2" alt="">
                    <i v-else class="fas fa-spinner fa-spin me-2"></i>
                    {{ launching ? 'Launching...' : 'Launch Play' }}
                </button>
                <div v-else data-bs-toggle="tooltip" title="You do not have privileges to complete this action">
                    <button class="btn btn-primary btn-primary-playbook" :disabled="true">
                        <img src="/img/icons/avid-icons/rocket-02.svg" class="icon me-2" alt="">Launch Play
                    </button>
                </div>
            </div>
        </div>
      </div>

      <!-- Right Column -->
      <div class="col-md-7 col-12">
        <!-- Settings Card -->
        <div class="card mb-3 settings-card" :class="{ 'gray-background': selected_group_id === null && selected_connection === null }">
            <div class="card-header">
                <h4 v-if="selected_group_id === null" class="mb-0">Settings</h4>
                <div v-else>
                    <h4 class="mb-0">Settings for 
                      <!-- Publish button for email -->
                        <span v-if="shouldShowPublishDropdown(selected_group_id)" class="dropdown me-2">
                          <button class="btn btn-secondary dropdown-toggle btn-sm" style="font-size: inherit;" type="button" data-bs-toggle="dropdown" @click.stop>
                              {{ selected_connection_name }}
                          </button>
                          <ul class="dropdown-menu dropdown-menu-primary">
                              <li v-for="connection in getConnectionsForGroup(selected_group_id)" :key="connection.id">
                                  <a class="dropdown-item" href="#" @click="publishToConnection(connection, selected_group_id, $event)">
                                      <img :src="connection.integration?.logo_url" class="icon me-2" style="filter:none">{{ connection.integration?.name }}
                                  </a>
                              </li>
                          </ul>
                        </span>
                        <span v-else>{{ selected_connection_name }}</span>
                      </h4>
                </div>
            </div>
            <div class="card-body">
                <!-- Loading state -->
                <div v-if="isLoadingConnectionInputs" class="text-center">
                    <div class="spinner-border spinner-border-sm me-2" role="status">
                        <span class="visually-hidden">Loading...</span>
                    </div>
                    <span>Loading {{ selected_connection_name }} Information...</span>
                </div>
                
                <!-- Cost warning -->
                <div v-if="connectionHasCost && selected_connection && selected_connection !== 'google_doc' && canEdit('billing')" class="alert alert-danger mb-3">
                    <p class="mb-0">Launching to this connection will result in an additional cost of ${{ costInfo.cost }}/month. You'll be asked to confirm this charge when you launch. (You can always launch to "Google Docs" for free.)</p>
                </div>
                
                <!-- Cost warning (no billing privileges) -->
                <div v-if="connectionHasCost && selected_connection && selected_connection !== 'google_doc' && !canEdit('billing')" class="alert alert-danger mb-3">
                    <p><strong>Launching to this connection comes with additional costs (${{ costInfo.cost }}/month), but you don't have privileges to make this authorization.</strong></p>
                    <p class="mb-0">Please switch to "Google Docs" to proceed with the launch.</p>
                </div>
                
                <!-- Connection inputs -->
                <div v-if="connection_inputs && connection_inputs.length > 0" class="connection-inputs">
                    <div class="row">
                        <div v-for="field in regularFields" :key="field.id" class="col-12" v-show="!shouldHideField(field)">
                            <deployment-field 
                                :field="field"
                                :loading-dependent-data="loadingDependentData"
                                :is-disabled="isDisabled(field)"
                                @select-change="handleSelectChange"
                                @change="onInputChange">
                            </deployment-field>
                        </div>
                    </div>
                    
                    <!-- Advanced Options Toggle Link -->
                    <div v-if="hasAdvancedFields" :class="['text-center', showAdvancedOptions ? 'mt-3' : '']">
                        <a href="#" class="btn btn-small btn-white" @click.prevent="showAdvancedOptions = !showAdvancedOptions">
                            <img :src="showAdvancedOptions ? '/img/icons/avid-icons/collapse.svg' : '/img/icons/avid-icons/expand.svg'" class="icon me-1" style="width: 16px" alt="">
                            {{ showAdvancedOptions ? 'Hide' : 'Show' }} Advanced Options
                        </a>
                    </div>
                    
                    <!-- Advanced fields -->
                    <div v-if="hasAdvancedFields && showAdvancedOptions" class="row mt-3">
                        <div v-for="field in advancedFields" :key="field.id" class="col-12" v-show="!shouldHideField(field)">
                            <deployment-field 
                                :field="field"
                                :loading-dependent-data="loadingDependentData"
                                :is-disabled="isDisabled(field)"
                                @select-change="handleSelectChange"
                                @change="onInputChange">
                            </deployment-field>
                        </div>
                    </div>
                </div>
                
                <!-- No inputs needed message -->
                <div v-if="selected_group_id && !isLoadingConnectionInputs && connection_inputs && connection_inputs.length === 0" class="text-center text-muted">
                    <p>No additional information is needed.</p>
                </div>
                
                <!-- No settings message -->
                <div v-if="selected_group_id && !isLoadingConnectionInputs && !connection_inputs" class="text-center text-muted">
                    <p>No settings available for the selected connection.</p>
                </div>
                
                <!-- Default message when no group selected -->
                <div v-if="!selected_group_id" class="text-center text-muted">
                    <p>Select a touchpoint to configure settings.</p>
                </div>
                
                <!-- Instructions/Warning messages -->
                <div v-if="instructions != null" class="mt-3">
                    <div class="alert alert-danger" role="alert" v-html="instructions"></div>
                </div>
            </div>
        </div>

        <!-- Preview Card -->
        <div class="card mb-3 preview-card" :class="{ 'gray-background': selected_group_id === null || preview_not_available || selected_connection === 'google_doc' }">
            <div class="card-header">
                <h4 class="mb-0">Preview</h4>
            </div>
            <div class="card-body">
                <!-- Loading state -->
                <div v-if="loading_preview" class="text-center">
                    <div class="spinner-border spinner-border-sm me-2" role="status">
                        <span class="visually-hidden">Loading...</span>
                    </div>
                    <span>Loading preview...</span>
                </div>
                
                <!-- Preview content -->
                <div v-else-if="preview_content" class="preview-content" v-html="preview_content">
                </div>
                
                <!-- Google Doc preview unavailable -->
                <div v-else-if="selected_connection === 'google_doc'" class="text-center text-muted">
                    <p>Preview is not available for Google Doc deployment.</p>
                </div>
                
                <!-- No preview available -->
                <div v-else-if="preview_not_available" class="text-center text-muted">
                    <p>No preview is available for {{ selected_connection_name }}</p>
                </div>
                
                <!-- Preview will be generated message -->
                <div v-else-if="selected_group_id && selected_connection" class="text-center text-muted">
                    <p>Preview will be generated once you have filled out all of the required fields above.</p>
                </div>
                
                <!-- Default message -->
                <div v-else class="text-center text-muted">
                    <p>Select a touchpoint and connection to see a preview.</p>
                </div>
            </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { usePlaybookStore } from '../../stores/playbook';
import { useClientStore } from '../../stores/client';
import axios from 'axios';
import Swal from 'sweetalert2';
import permissionsMixin from '../../mixins/permissions';
import DeploymentField from './DeploymentField.vue';
import DeploymentChecklist from './DeploymentChecklist.vue';
import statsigService from '../../services/statsig';

export default {
  name: 'PlayDeployView',
  mixins: [permissionsMixin],
  components: {
    DeploymentField,
    DeploymentChecklist
  },
  emits: ['back-to-builder'],
  props: {
    selectedAssets: {
      type: [Array, null],
      default: null
    }
  },
  data() {
    return {
      grouped_assets: {},
      selected_group_id: null,
      loading_connections: false,
      connection_inputs: null,
      loadingDependentData: false,
      preview_content: null,
      loading_preview: false,
      preview_not_available: false,
      selected_connections: {}, // Track selected connection for each asset type
      loading_connection_inputs: {}, // Track loading state for each connection
      previewDebounceTimer: null, // Timer for debouncing preview calls
      previewToken: 0, // Bumps on asset-type switch / new preview request so stale responses are ignored
      launching: false, // Track launching state
      showAdvancedOptions: false, // Track whether advanced options are shown
      costInfo: { cost: 0, included: false, free: false, loading: false, payment_options: [] }, // Cost information loaded from API
      paymentConfirmed: false, // Track if user has confirmed payment
      selectedPaymentOption: null, // Track selected payment option
      if_deployed: false, // Track whether to show deployment checklist
      deploymentDataForChecklist: null, // Store deployment data for checklist
      instructions: null, // Store instructions/warning messages from API
      launch_ui_rules_by_connection: {}, // connection_id => getLaunchUiRules() from deployment controller
      lastInstructionsConnectionId: null,
      loadingInstructions: false,
      checkConnectionsLoadedForKey: null, // Asset signature this page has already loaded
      checkConnectionsInFlight: {}, // Asset signature => in-flight promise
      connectionInputsCache: {}, // assetTypeId:connectionId => cached inputs
      connectionInputsInFlight: {} // assetTypeId:connectionId => in-flight promise
    };
  },
  setup() {
    const playbookStore = usePlaybookStore();
    const clientStore = useClientStore();
    return {
      playbookStore,
      clientStore
    };
  },
  computed: {
    client() {
      return this.clientStore.getClient;
    },
    playbook() {
      return this.playbookStore.getSelectedPlaybook;
    },
    selectedTouchpointCount() {
      const allAssets = (this.playbook?.assets && Array.isArray(this.playbook.assets)) ? this.playbook.assets : [];
      const filtered = Array.isArray(this.selectedAssets) && this.selectedAssets.length > 0
        ? allAssets.filter(a => this.selectedAssets.some(selected => selected.id === a.id))
        : allAssets;
      
      // Filter out draft touchpoints for the count
      const nonDraftAssets = filtered.filter(asset => !this.isDraftTouchpoint(asset));
      return nonDraftAssets.length;
    },
    selected_connection() {
      return this.selected_group_id ? this.selected_connections[this.selected_group_id] : null;
    },
    selected_connection_name() {
      if (!this.selected_connection) return '';
      
      // Find connection name from all groups
      for (const group of Object.values(this.grouped_assets)) {
        if (group.connections) {
          const connection = group.connections.find(c => c.id === this.selected_connection);
          if (connection) {
            return connection.integration?.name || connection.name || 'Connection';
          }
        }
      }
      
      return 'Connection';
    },
    isLoadingConnectionInputs() {
      // Check if we're still loading connection inputs for the selected group and connection
      if (!this.selected_group_id || !this.selected_connection) return false;
      
      // Check if this specific connection is currently loading
      return this.loading_connection_inputs[this.selected_connection] || false;
    },
    allGroupsReady() {
      // Check if all groups are Ready
      const groupIds = Object.keys(this.grouped_assets);
      if (groupIds.length === 0) return false;
      
      return groupIds.every(assetTypeId => this.getStatus(assetTypeId) === 'Ready');
    },
    regularFields() {
      if (!this.connection_inputs) return [];
      return this.connection_inputs.filter(field => !field.advanced);
    },
    advancedFields() {
      if (!this.connection_inputs) return [];
      return this.connection_inputs.filter(field => field.advanced === true);
    },
    hasAdvancedFields() {
      return this.advancedFields.length > 0;
    },
    connectionHasCost() {
      return this.costInfo && this.costInfo.cost > 0 && !this.costInfo.included && !this.costInfo.free;
    }
  },
  watch: {
    selectedAssets: {
      handler() {
        // Re-group assets and re-check connections when selection changes
        this.groupAssets();
        this.checkConnections();
        // reset selection-specific UI states
        this.previewToken++;
        this.selected_group_id = null;
        this.connection_inputs = null;
        this.preview_content = null;
        this.preview_not_available = false;
        this.selected_connections = {}; // Reset selected connections
        this.loading_connection_inputs = {}; // Reset loading states
        this.instructions = null; // Clear instructions
        this.launch_ui_rules_by_connection = {};
        this._launchUiRulesInFlight = {};
      },
      deep: false
    },
    connection_inputs: {
      handler(newInputs) {
        // Prevent re-entry: validateUtmTerm mutates field.error on the same objects
        // that connection_inputs references, which would re-trigger this watcher in a loop
        if (this._validatingUtmTerm) return;
        this._validatingUtmTerm = true;
        // Clear preview when inputs change - will be reloaded on change event
        this.preview_content = null;
        this.preview_not_available = false;
        
        // Validate UTM term in all fields
        this.validateUtmTerm();
        this.$nextTick(() => {
          this._validatingUtmTerm = false;
        });
      },
      deep: true
    }
  },
  mounted() {
    this.groupAssets();
    this.checkConnections();
    if (statsigService.getClient()) {
        statsigService.logEvent('playbook_preview', null, {
            playbook_id: this.playbook?.id,
            playbook_type_name: this.playbook?.playbook_type?.name,
        });
    }
    
  },
  beforeUnmount() {
    // Clear debounce timer to prevent memory leaks
    if (this.previewDebounceTimer) {
      clearTimeout(this.previewDebounceTimer);
    }
  },
  methods: {
    getSelectedAssetIdsForLaunch() {
      return Object.values(this.grouped_assets)
        .flat()
        .map(asset => asset.id);
    },
    getCheckConnectionsCacheKey() {
      const assetIds = this.getSelectedAssetIdsForLaunch();
      if (!assetIds || assetIds.length === 0) {
        return null;
      }
      return assetIds
        .slice()
        .sort((a, b) => Number(a) - Number(b))
        .join(',');
    },
    getConnectionInputsCacheKey(assetTypeId, connectionId) {
      return `${assetTypeId}:${connectionId}`;
    },
    goBackToBuilder() {
      this.$emit('back-to-builder');
    },
    async launchPlay() {
      if (this.launching || !this.allGroupsReady) return;
      
      // Disable button immediately to prevent double-clicks
      this.launching = true;
      
      // Check if any connection has additional cost
      let hasAdditionalCost = false;
      let paidConnection = null;
      for (const [assetTypeId, group] of Object.entries(this.grouped_assets)) {
        const selectedConnectionId = this.selected_connections[assetTypeId];
        if (selectedConnectionId && selectedConnectionId !== 'google_doc') {
          // Fetch cost info to check
          try {
            const costResponse = await axios.get(`/api/pathways/${this.client.url}/${selectedConnectionId}/cost`);
            if (costResponse.data.cost > 0 && !costResponse.data.included && !costResponse.data.free) {
              hasAdditionalCost = true;
              paidConnection = group.connections?.find(c => c.id === selectedConnectionId);
              break;
            }
          } catch (error) {
            console.error('Error checking cost:', error);
          }
        }
      }
      
      // If there's additional cost and user can't edit billing, show error
      if (hasAdditionalCost && !this.canEdit('billing')) {
        this.launching = false; // Re-enable button since we're stopping here
        Swal.fire({
          title: 'Billing Authorization Required',
          html: `<p class="mb-2">Launching to ${paidConnection?.integration?.name || 'this connection'} comes with additional costs, but you don't have privileges to make this authorization.</p><p class="mb-0">Please switch back to "Google Docs" to proceed with the launch.</p>`,
          icon: 'warning',
          customClass: {
            popup: 'swal-warning'
          }
        });
        return;
      }
      
      // If there's additional cost and user hasn't confirmed, show confirmation modal
      // Keep launching = true here because proceedWithLaunch will be called after confirmation
      if (hasAdditionalCost && !this.paymentConfirmed) {
        this.showPaymentConfirmation();
        return;
      }
      
      // Proceed with launch (this will prepare data and then show checklist)
      this.proceedWithLaunch();
    },
    selectGroup(assetTypeId) {
      const group = this.grouped_assets[assetTypeId];
      if (!group.connections) return;
      
      // Check if any connection for this asset type is loading
      const hasLoadingConnection = group.connections.some(connection => 
        this.loading_connection_inputs[connection.id] === true
      );
      if (hasLoadingConnection) return; // disable during load

      const previousGroupId = this.selected_group_id;
      if (previousGroupId !== null && String(previousGroupId) !== String(assetTypeId)) {
        this.previewToken++;
        this.preview_content = null;
        this.preview_not_available = false;
        this.loading_preview = false;
        if (this.previewDebounceTimer) {
          clearTimeout(this.previewDebounceTimer);
          this.previewDebounceTimer = null;
        }
      }

      this.selected_group_id = assetTypeId;
      this.showAdvancedOptions = false; // Reset advanced options visibility when switching groups
      this.instructions = null; // Clear instructions when switching groups

      // If there is an available selection for the connection, set it
      if (group.connections && group.connections.length > 0) {
        // Check if we have a previously selected connection for this asset type
        let connectionToSelect = null;
        
        if (this.selected_connections[assetTypeId]) {
          // Check if the previously selected connection still exists
          const previousConnection = group.connections.find(c => c.id === this.selected_connections[assetTypeId]);
          if (previousConnection) {
            connectionToSelect = this.selected_connections[assetTypeId];
          }
        }
        
        // If no previous selection or previous selection no longer exists, use first connection
        if (!connectionToSelect) {
          connectionToSelect = group.connections[0].id;
        }
        
        this.selected_connections[assetTypeId] = connectionToSelect;
        
        // Load the inputs for the selected connection
        this.fetchConnectionInputs(this.selected_connection);
        
        // Fetch cost information for this connection
        this.fetchConnectionCost();
      }
    },
    getConnectionStatus(assetTypeId) {
      // Check if any connection for this asset type is loading
      const group = this.grouped_assets[assetTypeId];
      if (group?.connections) {
        const hasLoadingConnection = group.connections.some(connection => 
          this.loading_connection_inputs[connection.id] === true
        );
        if (hasLoadingConnection) {
          return 'Loading...';
        }
      }
      
      // Dynamic status based on actual connection data
      if (!group || !group.connections || group.connections.length === 1) {
        return 'Unlinked';
      }
      
      return 'Linked';
    },
    getConnectionStatusClass(assetTypeId) {
      const status = this.getConnectionStatus(assetTypeId);
      if (status === 'Loading...') return '';
      return status === 'Linked' ? 'bg-green' : 'bg-yellow';
    },
    getConnectionStatusIcon(assetTypeId) {
      const status = this.getConnectionStatus(assetTypeId);
      if (status === 'Loading...') return 'fas fa-spinner fa-spin';
      return status === 'Linked' ? 'fas fa-link' : 'fas fa-unlink';
    },
    getStatus(assetTypeId) {
      const group = this.grouped_assets[assetTypeId];
      
      // Check if all required fields are filled and valid
      const selectedConnectionId = this.selected_connections[assetTypeId];
      if (!selectedConnectionId || !group.inputs || !group.inputs[selectedConnectionId]) {
        return 'Review';
      }
      
      const inputs = group.inputs[selectedConnectionId];
      
      // If no inputs (empty array) and there's only one connection, show as Ready
      if (inputs.length === 0 && group.connections && group.connections.length === 1) {
        return 'Ready';
      }
      
      // Check for field errors first
      const hasFieldErrors = inputs.some(field => field.error === true);
      if (hasFieldErrors) {
        return 'Review';
      }
      
      const allRequiredAndValid = inputs.every(field => {
        // Check if required field has a value
        if (field.required) {
          const hasValue = field.value !== null && field.value !== '' && field.value !== undefined;
          if (!hasValue) return false;
        }
        
        // Check regex validation if field has a value and regex pattern
        if (field.value && field.regex) {
          try {
            const regex = new RegExp(field.regex);
            return regex.test(field.value);
          } catch (error) {
            console.warn('Invalid regex pattern:', field.regex);
            return true; // Consider valid if regex is malformed
          }
        }
        
        return true;
      });
      
      return allRequiredAndValid ? 'Ready' : 'Review';
    },
    getStatusClass(assetTypeId) {
      const status = this.getStatus(assetTypeId);
      return status === 'Ready' ? 'bg-green' : 'bg-yellow';
    },
    getStatusIcon(assetTypeId) {
      const status = this.getStatus(assetTypeId);
      return status === 'Ready' ? 'fas fa-check' : 'fas fa-clock';
    },
    async checkConnections() {
      const cacheKey = this.getCheckConnectionsCacheKey();
      if (!cacheKey) {
        return;
      }

      // Only fetch once per page per selected asset signature.
      if (this.checkConnectionsLoadedForKey === cacheKey) {
        return;
      }

      // Reuse in-flight request for the same payload.
      if (this.checkConnectionsInFlight[cacheKey]) {
        await this.checkConnectionsInFlight[cacheKey];
        return;
      }
      
      this.loading_connections = true;
      this.checkConnectionsInFlight[cacheKey] = axios
        .post(`/api/playbooks/${this.client.url}/check_connections`, {
          asset_ids: this.getSelectedAssetIdsForLaunch()
        })
        .then(async (response) => {
          // Store connection data directly on grouped assets
          const steps = response.data.steps || {};
          const inputPromises = [];

          Object.keys(this.grouped_assets).forEach(assetTypeId => {
            if (steps[assetTypeId]) {
              this.grouped_assets[assetTypeId].connections = steps[assetTypeId].connections || [];
              this.grouped_assets[assetTypeId].inputs = {};
              
              // Immediately fetch inputs for all connections
              inputPromises.push(this.fetchAllConnectionInputs(assetTypeId, steps[assetTypeId].connections || []));
            }
          });

          await Promise.all(inputPromises);
          this.checkConnectionsLoadedForKey = cacheKey;
        })
        .catch(error => {
          console.error('Error checking connections:', error);
        })
        .finally(() => {
          delete this.checkConnectionsInFlight[cacheKey];
          this.loading_connections = false;
        });

      await this.checkConnectionsInFlight[cacheKey];
    },
    shouldShowPublishDropdown(group_id) {
      const group = this.grouped_assets[group_id];
      if (!group) return false;
      
      // Don't show dropdown while any connection is loading for this group
      if (group.connections) {
        const hasLoadingConnection = group.connections.some(connection => 
          this.loading_connection_inputs[connection.id] === true
        );
        if (hasLoadingConnection) return false;
      }
      
      const connectionCount = group.connections?.length || 0;
      // Show dropdown if there are connections available (including Google Docs option)
      return connectionCount > 1;
    },
    hasLoadingConnectionForGroup(assetTypeId) {
      const group = this.grouped_assets[assetTypeId];
      if (!group?.connections) return false;
      
      return group.connections.some(connection => 
        this.loading_connection_inputs[connection.id] === true
      );
    },
    getConnectionsForGroup(group_id) {
      const group = this.grouped_assets[group_id];
      return group?.connections || [];
    },
    publishToConnection(connection, assetTypeId, event) {
      // Prevent default link behavior
      event.preventDefault();
      
      // Reset advanced options visibility when switching connections
      this.showAdvancedOptions = false;
      
      // Set selected connection and load its inputs
      this.selected_connections[assetTypeId] = connection.id;
      this.fetchConnectionInputs(connection.id);
      
      // Fetch cost information for this connection
      this.fetchConnectionCost();
      
      // Clear previous preview
      this.preview_content = null;
      
      // Close the dropdown
      this.closeDropdown(event.target);
    },
    closeDropdown(element) {
      // Find the dropdown element and close it
      const dropdown = element.closest('.dropdown');
      if (dropdown) {
        const dropdownToggle = dropdown.querySelector('[data-bs-toggle="dropdown"]');
        if (dropdownToggle) {
          // Create and dispatch a click event to close the dropdown
          const clickEvent = new Event('click', { bubbles: true });
          dropdownToggle.dispatchEvent(clickEvent);
        }
      }
    },
    async fetchAllConnectionInputs(assetTypeId, connections) {
      // Set loading state for all connections
      connections.forEach(connection => {
        this.loading_connection_inputs[connection.id] = true;
      });
      
      // Get asset IDs for this asset type
      const assetIds = this.grouped_assets[assetTypeId].map(asset => asset.id);
      
      // Process inputs as they complete instead of waiting for all
      const emptyLaunchUiRules = { preview_minimum_field_ids_by_asset_type: {} };
      const emptyRulesPayload = { data: { launch_ui_rules: emptyLaunchUiRules } };

      const inputPromises = connections.map(async (connection) => {
        const cacheKey = this.getConnectionInputsCacheKey(assetTypeId, connection.id);

        // Use cached inputs to avoid repeated calls for the same asset type + connection.
        if (this.connectionInputsCache[cacheKey]) {
          if (!this.grouped_assets[assetTypeId].inputs) {
            this.grouped_assets[assetTypeId].inputs = {};
          }
          this.grouped_assets[assetTypeId].inputs[connection.id] = this.connectionInputsCache[cacheKey];
          this.loading_connection_inputs[connection.id] = false;

          if (this.selected_group_id === assetTypeId && this.selected_connections[assetTypeId] === connection.id) {
            this.connection_inputs = this.connectionInputsCache[cacheKey];
            this.$nextTick(() => {
              this.checkAndLoadDependentData();
            });
          }

          return { connectionId: connection.id, inputs: this.connectionInputsCache[cacheKey] };
        }

        // Reuse in-flight request for this asset type + connection.
        if (this.connectionInputsInFlight[cacheKey]) {
          const inFlightInputs = await this.connectionInputsInFlight[cacheKey];
          if (!this.grouped_assets[assetTypeId].inputs) {
            this.grouped_assets[assetTypeId].inputs = {};
          }
          this.grouped_assets[assetTypeId].inputs[connection.id] = inFlightInputs;
          this.loading_connection_inputs[connection.id] = false;
          return { connectionId: connection.id, inputs: inFlightInputs };
        }

        try {
          let rulesPromise;
          if (connection.id === 'google_doc') {
            rulesPromise = Promise.resolve(emptyRulesPayload);
          } else if (this.launch_ui_rules_by_connection[connection.id]) {
            rulesPromise = Promise.resolve({
              data: { launch_ui_rules: this.launch_ui_rules_by_connection[connection.id] },
            });
          } else {
            if (!this._launchUiRulesInFlight) {
              this._launchUiRulesInFlight = {};
            }
            const connectionKey = connection.id;
            if (!this._launchUiRulesInFlight[connectionKey]) {
              this._launchUiRulesInFlight[connectionKey] = axios
                .post(`/api/playbooks/${this.client.url}/deployment_launch_ui_rules`, {
                  connection_id: connectionKey,
                })
                .catch(() => emptyRulesPayload)
                .finally(() => {
                  delete this._launchUiRulesInFlight[connectionKey];
                });
            }
            rulesPromise = this._launchUiRulesInFlight[connectionKey];
          }

          this.connectionInputsInFlight[cacheKey] = Promise.all([
            axios.post(`/api/playbooks/${this.client.url}/connection_inputs`, {
              connection_id: connection.id,
              asset_type_id: assetTypeId,
              asset_ids: assetIds,
              all_asset_ids: Object.values(this.grouped_assets).flat().map(a => a.id),
              playbook_id: this.playbook.id
            }),
            rulesPromise
          ])
            .then(([response, rulesResponse]) => {
              const launchRules = rulesResponse.data.launch_ui_rules || emptyLaunchUiRules;
              this.launch_ui_rules_by_connection = {
                ...this.launch_ui_rules_by_connection,
                [connection.id]: launchRules
              };

              const inputs = response.data.inputs;
              // Initialize visibility for password fields
              inputs.forEach(field => {
                if (field.type === 'password' || field.type === 'select-other') {
                  field.visible = false;
                }
              });

              this.connectionInputsCache[cacheKey] = inputs;
              return inputs;
            })
            .finally(() => {
              delete this.connectionInputsInFlight[cacheKey];
            });

          const inputs = await this.connectionInputsInFlight[cacheKey];

          // Store inputs immediately as they arrive
          if (!this.grouped_assets[assetTypeId].inputs) {
            this.grouped_assets[assetTypeId].inputs = {};
          }
          this.grouped_assets[assetTypeId].inputs[connection.id] = inputs;
          
          // Clear loading state for this connection
          this.loading_connection_inputs[connection.id] = false;
          
          // If this connection is currently selected for this asset type, check for dependent data
          if (this.selected_group_id === assetTypeId && this.selected_connections[assetTypeId] === connection.id) {
            this.connection_inputs = inputs;
            this.$nextTick(() => {
              this.checkAndLoadDependentData();
            });
          }

          return { connectionId: connection.id, inputs };
        } catch (error) {
          console.error(`Error fetching inputs for connection ${connection.id}:`, error);
          
          // Clear loading state even on error
          this.loading_connection_inputs[connection.id] = false;
          
          // Store empty array on error
          if (!this.grouped_assets[assetTypeId].inputs) {
            this.grouped_assets[assetTypeId].inputs = {};
          }
          this.grouped_assets[assetTypeId].inputs[connection.id] = [];
          
          return { connectionId: connection.id, inputs: [] };
        }
      });
      
      // Wait for all promises (but process as they complete)
      await Promise.all(inputPromises);
      
      // Auto-select the first connection if there's only one option
      if (connections.length === 1 && !this.selected_connections[assetTypeId]) {
        this.selected_connections[assetTypeId] = connections[0].id;
        
        // If this group is currently selected, set connection_inputs and check for dependent data
        if (this.selected_group_id === assetTypeId) {
          const inputs = this.grouped_assets[assetTypeId].inputs?.[connections[0].id];
          if (inputs) {
            this.connection_inputs = inputs;
            this.$nextTick(() => {
              this.checkAndLoadDependentData();
            });
          }
        }
      }
    },
    fetchConnectionInputs(connectionId) {
      const cacheKey = this.getConnectionInputsCacheKey(this.selected_group_id, connectionId);

      // Get inputs from grouped_assets
      const group = this.grouped_assets[this.selected_group_id];
      if (group && group.inputs && group.inputs[connectionId]) {
        this.connection_inputs = group.inputs[connectionId];
        
        // Clear any pending debounced preview calls
        if (this.previewDebounceTimer) {
          clearTimeout(this.previewDebounceTimer);
        }
        
        // Auto-load preview if all required fields are filled (debounced)
        this.$nextTick(() => {
          if (this.canPreview()) {
            this.onInputChange();
          }
        });
        
        // Check for preset values and load dependent data
        this.checkAndLoadDependentData();
        
        // Fetch instructions/warnings for this connection
        this.getInstructions();
        return;
      }

      // Fallback to cached inputs if available
      if (this.connectionInputsCache[cacheKey]) {
        this.connection_inputs = this.connectionInputsCache[cacheKey];
        if (!this.grouped_assets[this.selected_group_id].inputs) {
          this.grouped_assets[this.selected_group_id].inputs = {};
        }
        this.grouped_assets[this.selected_group_id].inputs[connectionId] = this.connectionInputsCache[cacheKey];
        this.checkAndLoadDependentData();
        this.getInstructions();
        return;
      }

      // Set loading state for this specific connection
      this.loading_connection_inputs[connectionId] = true;

      // Get asset IDs for this asset type
      const assetIds = this.grouped_assets[this.selected_group_id].map(asset => asset.id);

      if (!this.connectionInputsInFlight[cacheKey]) {
        this.connectionInputsInFlight[cacheKey] = axios.post(`/api/playbooks/${this.client.url}/connection_inputs`, {
          connection_id: connectionId,
          asset_type_id: this.selected_group_id,
          asset_ids: assetIds,
          all_asset_ids: Object.values(this.grouped_assets).flat().map(a => a.id),
          playbook_id: this.playbook.id
        })
          .finally(() => {
            delete this.connectionInputsInFlight[cacheKey];
          });
      }

      // Fallback to API call if not in grouped_assets (shouldn't happen normally)
      this.connectionInputsInFlight[cacheKey]
      .then(response => {
        const inputs = response.data.inputs;
        // Initialize visibility for password fields
        inputs.forEach(field => {
          if (field.type === 'password' || field.type === 'select-other') {
            field.visible = false;
          }
        });
        this.connectionInputsCache[cacheKey] = inputs;
        this.connection_inputs = inputs;
        
        // Store in grouped_assets for future use
        if (!this.grouped_assets[this.selected_group_id].inputs) {
          this.grouped_assets[this.selected_group_id].inputs = {};
        }
        this.grouped_assets[this.selected_group_id].inputs[connectionId] = inputs;
        
        // Clear loading state for this connection
        this.loading_connection_inputs[connectionId] = false;
        
        // Clear any pending debounced preview calls
        if (this.previewDebounceTimer) {
          clearTimeout(this.previewDebounceTimer);
        }
        
        // Auto-load preview if all required fields are filled (debounced)
        this.$nextTick(() => {
          if (this.canPreview()) {
            this.onInputChange();
          }
        });
        
        // Check for preset values and load dependent data
        this.checkAndLoadDependentData();
        
        // Fetch instructions/warnings for this connection
        this.getInstructions();
      })
      .catch(error => {
        console.error('Error fetching connection inputs:', error);
        this.connection_inputs = null;
        // Clear loading state even on error
        this.loading_connection_inputs[connectionId] = false;
      });
    },
    handleSelectChange(field) {
      // Find all fields that depend on this field
      const dependentFields = this.connection_inputs.filter(f => f.depends_on === field.id);
      
      if (dependentFields.length > 0) {
        this.loadDependentData(dependentFields);
      }
    },
    loadDependentData(dependentFields) {
      // Get the parent field value from the first dependent field (they all depend on the same field)
      const parentField = this.connection_inputs.find(f => f.id === dependentFields[0].depends_on);
      if (!parentField || !parentField.value) {
        // Reset all dependent fields if parent is cleared
        dependentFields.forEach(field => {
          const dependentField = this.connection_inputs.find(f => f.id === field.id);
          if (dependentField) {
            dependentField.options = {};
            dependentField.value = '';
          }
        });
        return;
      }

      this.loadingDependentData = true;

      // Call API to get dependent data
      axios.post(`/api/playbooks/${this.client.url}/connection_inputs`, {
        connection_id: this.selected_connection,
        parent_value: parentField.value,
        id: parentField.id
      })
      .then(response => {

        if(response.data.value) {
          dependentFields.forEach(field => {
            const dependentField = this.connection_inputs.find(f => f.id === field.id);
            if (dependentField) {
              dependentField.value = response.data.value;
            }
          });
        }
        else {
          // Add a default "All Contacts" option
          const options = {
            ...response.data.options
          };

          // Update all dependent fields with the same options
          dependentFields.forEach(field => {
            const dependentField = this.connection_inputs.find(f => f.id === field.id);
            if (dependentField) {
              const previousValue = dependentField.value;
              dependentField.options = options;
              if (previousValue !== undefined && previousValue !== null && Object.prototype.hasOwnProperty.call(options, previousValue)) {
                dependentField.value = previousValue;
              } else {
                // Set default value to "All Contacts"
                dependentField.value = '';
              }
            }
          });
        }
      })
      .catch(error => {
        console.error('Error loading dependent data:', error);
        // Reset all dependent fields on error
        dependentFields.forEach(field => {
          const dependentField = this.connection_inputs.find(f => f.id === field.id);
          if (dependentField) {
            dependentField.options = {
              '': 'All Contacts'
            };
            dependentField.value = '';
          }
        });
      })
      .finally(() => {
        this.loadingDependentData = false;
      });
    },
    checkAndLoadDependentData() {
      // Check all fields that have a value and see if any other fields depend on them
      if (!this.connection_inputs) return;
      
      // Find all fields that have a value and check for dependent fields
      this.connection_inputs.forEach(field => {
        // Only check fields that have a value (preset)
        if (field.value && field.value !== '' && field.value !== null && field.value !== undefined) {
          // Find all fields that depend on this field
          const dependentFields = this.connection_inputs.filter(f => f.depends_on === field.id);
          
          if (dependentFields.length > 0) {
            // Load dependent data for these fields
            this.loadDependentData(dependentFields);
          }
        }
      });
    },
    isDisabled(field) {
      // If there's a cost and user can't edit billing, disable all fields
      if (this.connectionHasCost && this.selected_connection && this.selected_connection !== 'google_doc' && !this.canEdit('billing')) {
        return true;
      }
      
      if (!field.disable_logic) return false;
      try {
        // Create a context object with the data needed for evaluation
        const context = {
          connection_inputs: this.connection_inputs || []
        };
        
        // Create a function that uses the context
        const evaluateWithContext = new Function('context', `
          const connection_inputs = context.connection_inputs;
          return ${field.disable_logic};
        `);
        
        return evaluateWithContext(context);
      } catch (e) {
        console.error('Error evaluating disable logic:', e);
        return false;
      }
    },
    shouldHideField(field) {
      // Don't hide required select fields with no options - they need to show an error
      if (field.type === 'select' && field.required && (!field.options || Object.keys(field.options).length === 0)) {
        return false;
      }
      
      if (!field.hide_logic) return false;
      try {
        // Create a context object with the data needed for evaluation
        const context = {
          connection_inputs: this.connection_inputs || []
        };
        
        // Create a function that uses the context
        const evaluateWithContext = new Function('context', `
          const connection_inputs = context.connection_inputs;
          return ${field.hide_logic};
        `);
        
        return evaluateWithContext(context);
      } catch (e) {
        console.error('Error evaluating hide logic:', e);
        return false;
      }
    },
    deploymentFieldHasNonEmptyValue(field) {
      if (!field || field.value === null || field.value === undefined) return false;
      if (Array.isArray(field.value)) return field.value.length > 0;
      if (typeof field.value === 'boolean') return true;
      return String(field.value).trim() !== '';
    },
    deploymentLaunchUsesRelaxedPreviewRules() {
      if (!this.connection_inputs || !this.selected_connection || this.selected_group_id === null || this.selected_group_id === undefined) {
        return false;
      }
      const rules = this.launch_ui_rules_by_connection[this.selected_connection];
      if (!rules || !rules.preview_minimum_field_ids_by_asset_type) return false;
      const byType = rules.preview_minimum_field_ids_by_asset_type;
      const minimumIds = byType[this.selected_group_id] ?? byType[String(this.selected_group_id)];
      if (!minimumIds || !Array.isArray(minimumIds) || minimumIds.length === 0) return false;
      return minimumIds.every(id => {
        const field = this.connection_inputs.find(f => f.id === id);
        return field && this.deploymentFieldHasNonEmptyValue(field);
      });
    },
    canPreview() {
      // If there's a cost and user can't edit billing, disable preview
      if (this.connectionHasCost && this.selected_connection && this.selected_connection !== 'google_doc' && !this.canEdit('billing')) {
        return false;
      }
      
      if(this.selected_connection === 'google_doc') {
        return false;
      }
      const relaxedPreview = this.deploymentLaunchUsesRelaxedPreviewRules();
      // Check if all required fields are filled out and valid (or relaxed rules from integration)
      if (this.connection_inputs) {
        return this.connection_inputs.every(field => {
          if (field.required && !relaxedPreview) {
            if (!this.deploymentFieldHasNonEmptyValue(field)) return false;
          }
          
          // Check regex validation if field has a value and regex pattern
          if (field.value && field.regex) {
            const valueToTest = Array.isArray(field.value) ? field.value.join(',') : field.value;
            try {
              const regex = new RegExp(field.regex);
              return regex.test(valueToTest);
            } catch (error) {
              console.warn('Invalid regex pattern:', field.regex);
              return true; // Consider valid if regex is malformed
            }
          }
          
          return true;
        });
      }
      
      return false;
    },
    loadPreview() {
      if (!this.canPreview() || this.loading_preview) return;

      const snapshot = this.previewToken;

      this.loading_preview = true;
      this.preview_not_available = false;

      // Get the first asset from the selected group
      const selectedGroup = this.grouped_assets[this.selected_group_id];
      if (!selectedGroup || selectedGroup.length === 0) {
        if (snapshot === this.previewToken) {
          this.loading_preview = false;
        }
        return;
      }

      const assetId = selectedGroup[0].id;

      // Create form data
      const formData = new FormData();
      formData.append('asset_id', assetId);

      // Add all input fields
      this.connection_inputs.forEach(field => {
        formData.append(field.id, field.value || '');
      });

      // Make the preview request
      axios.post(`/playbooks/${this.client.url}/preview/${this.selected_connection}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        }
      })
      .then(response => {
        if (snapshot !== this.previewToken) {
          return;
        }
        this.preview_content = response.data;
      })
      .catch(error => {
        if (snapshot !== this.previewToken) {
          return;
        }
        if (error.response && error.response.status === 418) {
          this.preview_not_available = true;
          this.preview_content = null;
        } else {
          this.preview_content = '<div class="alert alert-danger">Error loading preview. Please try again.</div>';
          this.preview_not_available = false;
        }
      })
      .finally(() => {
        if (snapshot !== this.previewToken) {
          return;
        }
        this.loading_preview = false;
      });
    },
    onInputChange() {
      // Validate UTM term immediately when input changes
      this.validateUtmTerm();
      
      // Clear any existing debounce timer
      if (this.previewDebounceTimer) {
        clearTimeout(this.previewDebounceTimer);
      }
      
      // Set a new debounce timer to load preview after 500ms of no changes
      // Increased delay to pool multiple field changes together
      this.previewDebounceTimer = setTimeout(() => {
        // Load preview when input changes and all required fields are filled
        if (this.canPreview() && this.selected_connection && !this.loading_preview) {
          this.loadPreview();
        }
      }, 500);
    },
    groupAssets() {
      const allAssets = (this.playbook?.assets && Array.isArray(this.playbook.assets)) ? this.playbook.assets : [];
      let filtered = Array.isArray(this.selectedAssets) && this.selectedAssets.length > 0
        ? allAssets.filter(a => this.selectedAssets.some(selected => selected.id === a.id))
        : allAssets;
      
      // Filter out draft touchpoints (no inputs or all inputs blank)
      filtered = filtered.filter(asset => !this.isDraftTouchpoint(asset));
      
      this.grouped_assets = filtered.reduce((groups, asset) => {
        const assetTypeId = asset?.asset_type?.id;
        if (!groups[assetTypeId]) {
          groups[assetTypeId] = [];
        }
        groups[assetTypeId].push(asset);
        return groups;
      }, {});
    },
    validateUtmTerm() {
      // Validate all groups for UTM term errors
      Object.keys(this.grouped_assets).forEach(assetTypeId => {
        const group = this.grouped_assets[assetTypeId];
        if (!group.inputs) return;
        
        Object.keys(group.inputs).forEach(connectionId => {
          const inputs = group.inputs[connectionId];
          
          // Validate each field
          inputs.forEach(field => {
            // Only validate text and textarea fields
            if ((field.type === 'text' || field.type === 'textarea') && field.value) {
              if (field.value.toLowerCase().includes('utm_term')) {
                field.error = true;
                field.error_msg = 'The UTM Term variable has been reserved for use with Avid reporting. You cannot use it for links generated with Avid.';
              } else {
                // Clear error if utm_term is removed
                if (field.error_msg === 'The UTM Term variable has been reserved for use with Avid reporting. You cannot use it for links generated with Avid.') {
                  field.error = false;
                  field.error_msg = '';
                }
              }
            }
          });
        });
      });
    },
    fetchConnectionCost() {
      if (!this.selected_connection || this.selected_connection === 'google_doc') {
        this.costInfo = { cost: 0, included: false, free: false, loading: false, payment_options: [] };
        return;
      }
      
      this.costInfo.loading = true;
      axios.get(`/api/pathways/${this.client.url}/${this.selected_connection}/cost`)
        .then(response => {
          this.costInfo = { ...response.data, loading: false };
        })
        .catch(error => {
          console.error('Error fetching connection cost:', error);
          this.costInfo = { cost: 0, included: false, free: false, loading: false, payment_options: [] };
        });
    },
    getInstructions() {
      // Skip instructions for google_doc
      if (!this.selected_connection || this.selected_connection === 'google_doc') {
        this.instructions = null;
        this.lastInstructionsConnectionId = null;
        return;
      }

      // Prevent duplicate calls for the same selected connection.
      if (this.loadingInstructions || this.lastInstructionsConnectionId === this.selected_connection) {
        return;
      }

      this.loadingInstructions = true;
      
      axios.get(`/api/pathways/${this.selected_connection}/get_instructions`)
        .then(response => {
          this.instructions = response.data.instructions;
          this.lastInstructionsConnectionId = this.selected_connection;
        })
        .catch(error => {
          console.error('Error fetching instructions:', error);
          this.instructions = null;
          this.lastInstructionsConnectionId = null;
        })
        .finally(() => {
          this.loadingInstructions = false;
        });
    },
    showPaymentConfirmation() {
      // Get the connection with cost
      let costConnection = null;
      let costAmount = 0;
      let paymentOptions = {};
      
      for (const [assetTypeId, group] of Object.entries(this.grouped_assets)) {
        const selectedConnectionId = this.selected_connections[assetTypeId];
        if (selectedConnectionId && selectedConnectionId !== 'google_doc') {
          // Find the connection details
          const connection = group.connections?.find(c => c.id === selectedConnectionId);
          if (connection) {
            costConnection = connection;
            
            // Fetch cost for this connection
            axios.get(`/api/pathways/${this.client.url}/${selectedConnectionId}/cost`)
              .then(response => {
                costAmount = response.data.cost;
                paymentOptions = response.data.payment_options || {};
                this.displayPaymentModal(costConnection, costAmount, paymentOptions);
              })
              .catch(error => {
                console.error('Error fetching connection cost:', error);
              });
          }
          break;
        }
      }
    },
    displayPaymentModal(connection, cost, paymentOptions) {
      // If no payment options available, prompt user to add billing information
      if (!paymentOptions || Object.keys(paymentOptions).length === 0) {
        this.launching = false; // Re-enable button since we're stopping here
        Swal.fire({
          title: 'Billing Information Required',
          html: `<p class="mb-2">Before you can launch this playbook directly to ${connection.integration?.name} with Avid Relay, you need to add a payment method. Avid Relay provides unlimited Pathways and allows you to deploy Playbooks directly to your advertising and marketing systems. It will result in an additional charge of $${cost} per month.</p><p><a class="btn btn-primary" href="/settings/${this.client.url}/billing">Go to your Billing Settings</a></p>`,
          icon: 'warning',
          showConfirmButton: false,
          customClass: {
            popup: 'swal-warning'
          }
        });
        return;
      }
      
      let htmlContent = `<p>By launching this Playbook, you are confirming that you are authorizing Avid to charge your account an additional $${cost} per month in subsequent invoices. This will provide you unlimited Pathways and allow you to deploy Playbooks directly to your advertising and marketing systems.</p>`;
      
      const optsArray = Array.isArray(paymentOptions) ? paymentOptions : Object.entries(paymentOptions);
      if (optsArray.length > 1) {
        htmlContent += '<p>Please select which account you would like to charge.</p>';
        optsArray.forEach((opt, idx) => {
          const [key, value] = Array.isArray(opt) ? opt : [opt, opt];
          htmlContent += `<div class="form-check text-start mb-2"><input class="form-check-input" type="radio" name="paymentOpt" id="paymentOpt${idx}" value="${key}" ${idx === 0 ? 'checked' : ''} aria-label="Paymentopt"><label class="form-check-label ms-2" for="paymentOpt${idx}">${value}</label></div>`;
        });
      } else {
        const [key, value] = Array.isArray(optsArray[0]) ? optsArray[0] : [optsArray[0], optsArray[0]];
        htmlContent += `<p class="fw-bold text-center">This will be charged to ${value}</p>`;
      }
      htmlContent += `<div class="form-check mt-3 text-start"><input class="form-check-input" type="checkbox" id="confirmCharge" aria-label="Checkbox"><label class="form-check-label ms-2" for="confirmCharge">I understand I will be charged an additional $${cost} per month for Avid Relay on subsequent invoices.</label></div>`;
      
      Swal.fire({
        title: 'Are you sure you want to proceed?',
        html: htmlContent,
        showCancelButton: true,
        confirmButtonText: 'Confirm',
        focusConfirm: false,
        didOpen: () => {
          const confirmButton = Swal.getConfirmButton();
          const checkbox = Swal.getPopup().querySelector('#confirmCharge');
          
          // Initially disable the confirm button
          confirmButton.disabled = true;
          
          // Add event listener to enable/disable button based on checkbox state
          checkbox.addEventListener('change', () => {
            confirmButton.disabled = !checkbox.checked;
          });
        },
        preConfirm: () => {
          const checkbox = Swal.getPopup().querySelector('#confirmCharge');
          if (!checkbox.checked) {
            Swal.showValidationMessage('Please confirm the charge.');
            return false;
          }
          let selectedOpt = null;
          const radio = Swal.getPopup().querySelector('input[name="paymentOpt"]:checked');
          if (radio) {
            selectedOpt = radio.value;
          } else if (optsArray.length === 1) {
            selectedOpt = Array.isArray(optsArray[0]) ? optsArray[0][0] : optsArray[0];
          }
          return { payment_option: selectedOpt };
        },
        customClass: {
          popup: 'swal-success',
          htmlContainer: 'text-start'
        }
      }).then((result) => {
        if (result.isConfirmed) {
          this.paymentConfirmed = true;
          this.selectedPaymentOption = result.value ? result.value.payment_option : null;
          // Proceed with launch after confirmation (will show checklist after data is ready)
          this.proceedWithLaunch();
        } else {
          // User cancelled - re-enable the button
          this.launching = false;
        }
      });
    },
    async proceedWithLaunch() {
      // This method now contains the original launch logic
      try {
        // Prepare the connections data for the API call
        const connectionsMap = {}; // Use a map to consolidate by connection_id
        const exportId = Date.now().toString(); // Generate unique export ID
        
        // Loop through each asset type group and collect connection data
        Object.keys(this.grouped_assets).forEach(assetTypeId => {
          const group = this.grouped_assets[assetTypeId];
          const selectedConnectionId = this.selected_connections[assetTypeId];
          
          if (selectedConnectionId && group.inputs && group.inputs[selectedConnectionId]) {
            // Get asset IDs for this group
            const assetIds = group.map(asset => asset.id);
            
            // Get connection name
            let connectionName = 'Unknown Connection';
            if (selectedConnectionId === 'google_doc') {
              connectionName = 'Google Docs';
            } else {
              const connection = group.connections?.find(c => c.id === selectedConnectionId);
              connectionName = connection?.integration?.name || connection?.name || 'Unknown Connection';
            }
            
            // Prepare inputs data
            const inputs = {};
            group.inputs[selectedConnectionId].forEach(field => {
              inputs[field.id] = field.value || '';
            });
            
            // Check if this connection already exists in the map
            if (connectionsMap[selectedConnectionId]) {
              // Add asset IDs to existing connection
              connectionsMap[selectedConnectionId].asset_ids.push(...assetIds);
              // Merge inputs (later inputs override earlier ones if same key)
              Object.assign(connectionsMap[selectedConnectionId].inputs, inputs);
            } else {
              // Create new connection entry
              connectionsMap[selectedConnectionId] = {
                connection_id: selectedConnectionId,
                connection_name: connectionName,
                asset_ids: assetIds,
                inputs: inputs
              };
            }
          }
        });
        
        // Convert map to array
        const connections = Object.values(connectionsMap);
        
        // Track Google Docs export from deployment if present
        const googleDocConnection = connections.find(conn => conn.connection_id === 'google_doc');
        if (googleDocConnection && statsigService.getClient()) {
            statsigService.logEvent('playbook_exported_to_google_docs', null, {
                playbook_id: this.playbook?.id,
                playbook_type_name: this.playbook?.playbook_type?.name,
                source: 'deployment',
                asset_count: googleDocConnection.asset_ids?.length || 0
            });
        }
        
        // Prepare deployment data for checklist (before API call so it's available immediately)
        this.deploymentDataForChecklist = {
          export_id: exportId, // Base export ID for Echo listeners
          export_ids: {}
        };
        
        // Transform connections data into the format expected by DeploymentChecklist
        connections.forEach(conn => {
          this.deploymentDataForChecklist.export_ids[conn.connection_id] = {
            export_id: `${conn.connection_id}_${exportId}`,
            connection_name: conn.connection_name,
            asset_ids: conn.asset_ids
          };
        });
        
        // Show deployment checklist NOW that data is ready
        this.if_deployed = true;
        
        // Wait for component to mount and then trigger listener setup
        await this.$nextTick();
        
        // Make the API call to deploy
        const response = await axios.post(`/api/playbooks/${this.client.url}/deploy/${this.playbook.url}`, {
          export_id: exportId,
          connections: connections
        });
        
        // Track playbook launch
        if (statsigService.getClient()) {
            statsigService.logEvent('playbook_launched', null, {
                playbook_id: this.playbook?.id,
                playbook_type_name: this.playbook?.playbook_type?.name,
                connection_count: connections.length
            });
        }
        
        this.launching = false;
        
      } catch (error) {
        console.error('Error launching play:', error);
        
        // Show more specific error message
        let errorMessage = 'Failed to launch play. Please try again.';
        if (error.response && error.response.data && error.response.data.error) {
          errorMessage = error.response.data.error;
        } else if (error.response && error.response.status === 422) {
          errorMessage = 'Please check all required fields and try again.';
        } else if (error.response && error.response.status === 403) {
          errorMessage = 'You do not have permission to launch this play.';
        }
        
        alert(errorMessage);
        this.launching = false;
      }
    },
    isDraftTouchpoint(touchpoint) {
      // A touchpoint is in draft mode if it has no inputs or all inputs are blank
      if (!touchpoint.inputs) {
        return true;
      }
      
      // Handle both object and array formats
      let inputs = [];
      if (Array.isArray(touchpoint.inputs)) {
        inputs = touchpoint.inputs;
      } else if (typeof touchpoint.inputs === 'object') {
        // Convert object to array of input objects
        inputs = Object.values(touchpoint.inputs);
      } else {
        return true; // Invalid format, treat as draft
      }
      
      // If no inputs at all, it's draft
      if (inputs.length === 0) {
        return true;
      }
      
      // Check if all inputs are blank (empty, null, undefined, or only whitespace)
      const hasNonBlankInput = inputs.some(input => {
        if (!input || typeof input !== 'object') return false;
        
        // Get the value - could be input.value or just input if it's a plain value
        let value = input.value !== undefined ? input.value : null;
        
        // If still no value, check if input itself is a string/number
        if (value === null && typeof input !== 'object') {
          value = input;
        }
        
        if (value === null || value === undefined) return false;
        
        const strValue = String(value).trim();
        
        // Exclude citations and link placeholders as they don't count as content
        const placeholder = input.placeholder || null;
        if (placeholder === 'citations' || placeholder === 'link') {
          return false;
        }
        
        return strValue.length > 0;
      });
      
      return !hasNonBlankInput;
    }
  }
};
</script>